import { Button } from "@/components/ui/button";
import { FormField } from "@/components/companies/FormField";
import { useLanguage } from "@/contexts/LanguageContext";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PasswordChangeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  passwordTouched: {
    current: boolean;
    new: boolean;
    confirm: boolean;
  };
  isSubmitting: boolean;
  onPasswordFieldChange: (field: string, value: string) => void;
  onPasswordBlur: (field: string) => void;
  onPasswordSubmit: () => Promise<void>;
}

export const PasswordChangeDialog = ({
  isOpen,
  onOpenChange,
  currentPassword,
  newPassword,
  confirmPassword,
  passwordError,
  passwordTouched,
  isSubmitting,
  onPasswordFieldChange,
  onPasswordBlur,
  onPasswordSubmit,
}: PasswordChangeDialogProps) => {
  const { t } = useLanguage();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {t("companyForm.password.confirmChangeTitle")}
          </DialogTitle>
          <DialogDescription>{t("companyForm.password.confirmChangeDescription")}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Alert className="bg-amber-50 border-amber-200">
            <AlertDescription className="text-amber-800">
              {t("companyForm.password.confirmChangeWarning")}
            </AlertDescription>
          </Alert>

          <div className="space-y-4 mt-4">
            <FormField
              label={t("companyForm.password.current")}
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => onPasswordFieldChange("current", e.target.value)}
              placeholder={t("companyForm.password.currentPlaceholder")}
              validation={{ isValid: true, error: "", touched: passwordTouched.current }}
              onBlur={() => onPasswordBlur("current")}
            />

            <FormField
              label={t("companyForm.password.new")}
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => onPasswordFieldChange("new", e.target.value)}
              placeholder={t("companyForm.password.newPlaceholder")}
              validation={{ isValid: true, error: "", touched: passwordTouched.new }}
              onBlur={() => onPasswordBlur("new")}
            />

            <FormField
              label={t("companyForm.password.confirm")}
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => onPasswordFieldChange("confirm", e.target.value)}
              placeholder={t("companyForm.password.confirmPlaceholder")}
              validation={{ isValid: true, error: "", touched: passwordTouched.confirm }}
              onBlur={() => onPasswordBlur("confirm")}
            />

            {/* Show real-time password match feedback */}
            {newPassword && confirmPassword && (
              <div className="mt-1 mb-2">
                {newPassword === confirmPassword ? (
                  <div className="flex items-center gap-1 text-xs text-teal-600">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>{t("validation.company.password.match")}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{t("validation.company.password.mismatch")}</span>
                  </div>
                )}
              </div>
            )}

            {/* Show error message alert only for errors other than password mismatch */}
            {passwordError && passwordError !== t("validation.company.password.mismatch") && (
              <Alert variant="destructive">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("companyForm.buttons.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onPasswordSubmit}
            disabled={isSubmitting}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isSubmitting ? t("companyForm.password.updating") : t("companyForm.password.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
